import i18n from './i18n';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { supabase } from './supabase';
import { c, NAV_ITEMS } from './dashboard/constants';
import { groupPatients } from './dashboard/utils';
import { buildPrintHtml } from './dashboard/printSummary';
import Sidebar from './dashboard/Sidebar';
import { _trackerCache } from './dashboard/trackerCache';
import { dismissAlertMutation, reactivateAlertMutation, reassignDoctorMutation } from './dashboard/alertActions';
import AssignModal from './dashboard/modals/AssignModal';
import LoginScreen from './dashboard/auth/LoginScreen';
import SetPasswordScreen from './dashboard/auth/SetPasswordScreen';
import CompleteClinicSignupScreen from './dashboard/auth/CompleteClinicSignupScreen';
import MfaGate from './dashboard/auth/MfaGate';
import ClinicalRoleAttestationScreen from './dashboard/auth/ClinicalRoleAttestationScreen';
import VCPRAttestationModal from './dashboard/modals/VCPRAttestationModal';
import { CLINICAL_ATTESTATION_VERSION } from './legalDocumentVersions';
import PermissionsView from './dashboard/views/PermissionsView';
import BillingView from './dashboard/views/BillingView';
import ClinicSettingsView from './dashboard/views/ClinicSettingsView';
import SupportView from './dashboard/views/SupportView';
import DashboardView from './dashboard/views/DashboardView';
import EnrollmentsView from './dashboard/views/EnrollmentsView';
import PatientDetailPanel from './dashboard/PatientDetailPanel';
import './App.css';



// ── Main App ───────────────────────────────────────────────────────────────────
export default function App() {
  const { t } = useTranslation();
  const [profile,      setProfile]      = useState(null);
  const [clinic,       setClinic]       = useState(null);
  const [membership,   setMembership]   = useState(null);
  const [subscription, setSubscription] = useState(null);
  // Separate from `subscription` itself (which starts null and stays null
  // for a genuinely-unsubscribed clinic, same value as "haven't fetched
  // yet") so BillingView can tell the two apart and avoid a load flash —
  // see its own use of this below.
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [authChecking, setAuthChecking] = useState(true);
  // 2026-08-27 (client-quality audit) — distinguishes "confirmed no
  // accessible session" (shows LoginScreen, unchanged) from "a query
  // failed or didn't finish in time" (shows a retryable error state
  // instead). Before this, hydrateProfile only read `{ data }` from every
  // query, and the auth effect's own 3s Promise.race flipped
  // authChecking to false whether hydration actually finished or the
  // timer just won — either way `profile` stayed null and a perfectly
  // valid session with a transient network/RLS blip looked identical to
  // "log in again."
  const [hydrationError, setHydrationError] = useState(null);
  // Clinic-onboarding audit finding: hydrateProfile only ever checked for
  // an *active* clinic_members row. A profile whose invite acceptance
  // previously failed (DOCTOR_SEAT_LIMIT_REACHED/MULTI_CLINIC_NOT_
  // SUPPORTED, from LoginScreen's own handleAcceptInvite) has a real,
  // still-pending (is_active=false, accepted_at null) row — but
  // hydrateProfile found no *active* row and just returned, leaving
  // `profile` unset and silently falling through to a bare LoginScreen on
  // every subsequent page load/session restore, with zero indication
  // anything was pending. The user had to blindly re-type their password
  // into the plain login form to re-trigger LoginScreen.handleSubmit's own
  // separate pending-invite query and get back to the real accept/decline
  // screen. This mirrors that same query at hydration time instead, so a
  // returning invitee (seats since freed up, or just closed the tab) lands
  // straight back on the accept screen.
  const [hydratedPendingInvite, setHydratedPendingInvite] = useState(null);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  // Self-serve clinic signup (2026-09-10, Bryan's explicit direction — no
  // verification gate). A valid session with no profiles row at all means
  // either a brand-new signup (the normal case this exists for) or a
  // stale/foreign auth.users row with no profile (the existing PGRST116
  // comment's pre-existing case, previously left silently on LoginScreen
  // with no explanation) — both land here now, both are actionable.
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);
  // Clinic data is not committed into the main application state until this
  // pending identity reaches AAL2. Keeping clinic=null prevents every clinic-
  // keyed fetch effect below from running behind the MFA screen.
  const [pendingMfaContext, setPendingMfaContext] = useState(null);
  const [patients,       setPatients]       = useState([]);
  const [patientsError,  setPatientsError]  = useState(false);
  const [wellnessPatients, setWellnessPatients] = useState([]);
  const [wellnessPatientsError, setWellnessPatientsError] = useState(false);
  const [pendingPets,    setPendingPets]    = useState([]);
  const [pendingError,   setPendingError]   = useState(false);
  const [rejectedPets,   setRejectedPets]   = useState([]);
  const [rejectedError,  setRejectedError]  = useState(false);
  const [rejectedExpanded, setRejectedExpanded] = useState(false);
  // 2026-08-27 (2026-08-16 dashboard audit, Medium QA item never fixed) —
  // Reject/Reverse had no busy-state guard: a fast double-click before
  // fetchPendingPets/fetchRejectedPets refreshed the list could fire the
  // handler twice for the same pet, duplicating the audit row and (for
  // Reject specifically) the owner's push notification. Per-pet, not a
  // single global flag, so acting on one pet's row doesn't block another.
  const [processingPetIds, setProcessingPetIds] = useState(() => new Set());
  const [workflows,      setWorkflows]      = useState([]);
  const [vets,           setVets]           = useState([]);
  // #9: condition_alert_profiles is global reference config (readable by
  // any authenticated user, not clinic-scoped — same posture as
  // survey_templates), fetched once like workflows/vets rather than
  // per-page. Maps condition_type -> the three recovery-count thresholds
  // so ConditionRow/PatientDetailPanel can show "N of M stable check-ins"
  // using the real per-condition-type value instead of a hardcoded 2.
  const [recoveryThresholds, setRecoveryThresholds] = useState({});
  const [loadingDashboard,    setLoadingDashboard]    = useState(false);
  const [loadingEnrollments,  setLoadingEnrollments]  = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [assigningPet,    setAssigningPet]    = useState(null);
  const [vcprPendingPet,  setVcprPendingPet]  = useState(null);
  const [assignLoading,   setAssignLoading]   = useState(false);
  const [assignError,     setAssignError]     = useState('');
  const [view,         setView]         = useState('dashboard');
  const [filters,      setFilters]      = useState({ sortBy: 'severity', clinician: '', conditionTypes: [] });
  const [lastUpdated,  setLastUpdated]  = useState(null);
  const [billingMsg,   setBillingMsg]   = useState(''); // success/cancel message after Stripe redirect
  const workflowsLoadedRef = useRef(false);
  const vetsLoadedRef      = useRef(false);
  const recoveryThresholdsLoadedRef = useRef(false);
  const dashboardRefreshRef   = useRef(null);
  const enrollmentsRefreshRef = useRef(null);

  // Browser tab title previously never changed from the static "Cairn —
  // Clinic Dashboard" set in public/index.html — every screen, including
  // the login form itself, showed the same title, and manifest.json (the
  // PWA/installed-app name) was still literally unedited "React App" /
  // "Create React App Sample" default CRA boilerplate. Reflects the
  // actual current screen instead, matching NAV_ITEMS' own labels so this
  // can't drift out of sync with the sidebar.
  useEffect(() => {
    if (authChecking) { document.title = i18n.t('common.titleLoading'); return; }
    if (needsPasswordSetup) { document.title = i18n.t('common.titleSetPassword'); return; }
    if (!profile) { document.title = i18n.t('common.titleLogin'); return; }
    const navItem = NAV_ITEMS.find(n => n.id === view);
    const navLabel = navItem ? i18n.t(navItem.labelKey) : i18n.t('nav.dashboard');
    document.title = i18n.t('common.titleWithView', { view: navLabel });
  }, [authChecking, needsPasswordSetup, profile, view]);

  // PatientDetailPanel's render (below) is gated only on `selectedPatient`
  // truthiness, not on `view` — it's a `position: fixed` overlay that sits
  // on top of whichever view is showing underneath, not scoped to any one
  // tab. Nothing previously cleared selectedPatient on a tab switch, so
  // opening a patient from Dashboard and then clicking Enrollments/Billing/
  // etc. left the panel visibly open over the new tab's content. Found live
  // 2026-08-10. Only fires on an actual view CHANGE (dependency array is
  // just [view]), not on every render, so this doesn't fight with
  // selectedPatient being set from within the dashboard view itself.
  useEffect(() => {
    setSelectedPatient(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Derived: clinic user's weight display preference (lbs by default)
  const useLbs = (profile?.weight_unit_pref ?? 'lbs') !== 'kg';

  // Filtering/sorting/grouping the full patient roster was previously
  // re-run on every App render regardless of whether patients or filters
  // actually changed — App owns many unrelated pieces of state (modals,
  // billing polling, etc.), so it re-renders often. Memoized here instead
  // of as plain consts further down (has to be above the authChecking/
  // needsPasswordSetup/!profile early returns below — a hook can't sit
  // after a conditional return without breaking the Rules of Hooks).
  const filtered = useMemo(() => {
    // Muted relationships (migration 183) are pulled out of the main
    // severity pipeline entirely — see mutedGroups below for their own
    // collapsed section — so a clinic that muted a patient never sees it
    // resurface in red/yellow/green just because a new alert fired.
    let result = patients.filter(p => !p.muted);
    if (filters.clinician) result = result.filter(p => p.assignedVet?.id === filters.clinician);
    if (filters.conditionTypes.length > 0) {
      // SRR is never independently assignable — it's auto-created alongside
      // CHF (and, since the IM workflow expansion, Chronic Respiratory
      // Disease too) specifically to monitor breathing rate as part of the
      // same clinical picture (handleAssign / assign_workflow_to_pet).
      // Filtering to either needs to include it: otherwise an elevated SRR
      // reading (a serious warning sign for both) could sit in red while a
      // CHF- or respiratory-filtered view shows green, because the SRR
      // condition row got excluded from the severity calculation entirely.
      const matchTypes = (filters.conditionTypes.includes('chf') || filters.conditionTypes.includes('chronic_respiratory'))
        ? [...new Set([...filters.conditionTypes, 'srr'])]
        : filters.conditionTypes;
      result = result.filter(p => matchTypes.includes(p.condition?.condition_type));
    }
    if (filters.sortBy === 'name') result.sort((a, b) => (a.pet?.name || '').localeCompare(b.pet?.name || ''));
    else if (filters.sortBy === 'lastUpdated') result.sort((a, b) => new Date(b.lastSurvey?.submitted_at || 0) - new Date(a.lastSurvey?.submitted_at || 0));
    else result.sort((a, b) => { const o = { immediate_review: 0, red: 1, yellow: 2, green: 3 }; return o[a.severity] - o[b.severity]; });
    return result;
  }, [patients, filters]);

  const grouped = useMemo(() => groupPatients(filtered), [filtered]);
  // Muted patients keep their real per-condition severity (still computed
  // above) — only which section they land in changes. groupPatients takes
  // the highest-severity condition per pet the same way the main pipeline
  // does; the muted flag itself isn't part of that shape, so it's read off
  // the raw `patients` rows here rather than threaded through groupPatients.
  const mutedGroups = useMemo(() => groupPatients(patients.filter(p => p.muted)), [patients]);

  // Apply this doctor's own default condition filter once, right after
  // login — keyed on profile?.id (not the profile object itself) so it
  // fires exactly once per session and never clobbers a filter change the
  // doctor makes afterward, even though profile gets a new object
  // reference on every other preference toggle (unit, language, etc).
  useEffect(() => {
    if (profile?.id && profile?.default_condition_filters?.length > 0) {
      setFilters(f => ({ ...f, conditionTypes: profile.default_condition_filters }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  // 2026-08-27 (client-quality audit) — all 3 handlers below used to
  // update local state optimistically and never check the mutation's
  // returned `{error}`, so a failed write (network blip, expired session)
  // left the UI silently showing a preference that was never actually
  // saved — it would look correct until the next reload/re-login quietly
  // reverted it. Now each rolls its optimistic state back to the prior
  // value and surfaces a generic error on failure, matching this file's
  // existing alert()/console.warn convention elsewhere.
  const handleToggleUnit = async (unit) => {
    const prev = profile?.weight_unit_pref;
    setProfile(p => ({ ...p, weight_unit_pref: unit }));
    if (profile?.id) {
      const { error } = await supabase.from('profiles').update({ weight_unit_pref: unit }).eq('id', profile.id);
      if (error) {
        console.warn('Failed to save weight_unit_pref:', error.message);
        setProfile(p => ({ ...p, weight_unit_pref: prev }));
        alert(t('common.error'));
      }
    }
  };

  const handleToggleLang = async (code) => {
    const prev = profile?.language_pref;
    setProfile(p => ({ ...p, language_pref: code }));
    i18n.changeLanguage(code);
    if (profile?.id) {
      const { error } = await supabase.from('profiles').update({ language_pref: code }).eq('id', profile.id);
      if (error) {
        console.warn('Failed to save language_pref:', error.message);
        setProfile(p => ({ ...p, language_pref: prev }));
        if (prev) i18n.changeLanguage(prev);
        alert(t('common.error'));
      }
    }
  };

  // Per-doctor (not per-clinic) default condition-filter view — a
  // specialist's own login always opens straight to their specialty,
  // independent of what any other doctor at the same clinic prefers.
  // Most doctors pick exactly one; some (internal medicine watching CHF
  // + OA, say) genuinely want more than one, hence the array.
  const handleSetDefaultConditionFilter = async (conditionTypes) => {
    const value = conditionTypes && conditionTypes.length > 0 ? conditionTypes : null;
    const prev = profile?.default_condition_filters;
    setProfile(p => ({ ...p, default_condition_filters: value }));
    if (profile?.id) {
      const { error } = await supabase.from('profiles').update({ default_condition_filters: value }).eq('id', profile.id);
      if (error) {
        console.warn('Failed to save default_condition_filters:', error.message);
        setProfile(p => ({ ...p, default_condition_filters: prev }));
        alert(t('common.error'));
      }
    }
  };

  // Auth
  useEffect(() => {
    checkAuth();
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setProfile(null); setClinic(null); setMembership(null); setSubscription(null);
        setPendingMfaContext(null);
        setSubscriptionLoaded(false); setSubscriptionError('');
        setNeedsPasswordSetup(false); setNeedsProfileSetup(false); setHydrationError(null);
        // Without this, declining a hydration-time pending-invite screen
        // (LoginScreen's handleDeclineInvite signs out, then clears its
        // own local pendingInvite state) would still leave this App-level
        // copy set — the very next render's `!profile && hydratedPending
        // Invite` check would immediately show the same invite screen
        // again instead of the plain login form the decline was supposed
        // to land on.
        setHydratedPendingInvite(null);
      }
      // Fires for both an invite-email link and a forgot-password link — Supabase
      // Auth treats both as "recovery" sessions. Either way, the person has a
      // valid session but no password set through the app yet (or, for a fresh
      // invite, may not have a profiles row yet either) — SetPasswordScreen
      // handles both cases with one form.
      if (event === 'PASSWORD_RECOVERY') { setAuthChecking(false); setNeedsPasswordSetup(true); }
      // Deliberately NOT awaited, deferred a tick via setTimeout — this is
      // the actual root cause of the dashboard-hang-on-tab-refocus bug,
      // confirmed by reading GoTrueClient's own source directly (not
      // guessed): GoTrueClient fires SIGNED_IN on ordinary tab refocus for
      // any valid, non-expiring session, not just real logins
      // (_recoverAndRefresh -> _notifyAllSubscribers('SIGNED_IN', ...)),
      // and that whole call happens WHILE the internal session lock is
      // still held (GoTrueClient._acquireLock, lockAcquired=true).
      // _notifyAllSubscribers awaits every onAuthStateChange callback
      // before returning, including this one — so directly awaiting
      // hydrateProfile()'s own Supabase queries here made this callback
      // (and therefore the outer lock-held _recoverAndRefresh call) wait
      // on hydrateProfile, while hydrateProfile's own _getAccessToken()
      // call — seeing the lock already held — queued behind that exact
      // same outer operation on GoTrueClient's internal pendingInLock
      // array. A genuine deadlock: the outer refresh waiting on
      // hydrateProfile, hydrateProfile waiting on the outer refresh to
      // release the lock it's still holding. No fetch timeout or lock-
      // execution timeout can reach this — the stuck `await` is a plain
      // internal promise chain, not a network call, and it isn't inside
      // the pluggable `lock` option's own callback either (see
      // supabase.js's boundedLock comment for the two failed attempts at
      // bounding that instead). This is Supabase's own documented
      // pitfall: never call another Supabase query directly inside
      // onAuthStateChange — defer it out of the callback so
      // _notifyAllSubscribers's Promise.all doesn't wait on it, letting
      // the lock release normally; hydrateProfile's own queries then run
      // afterward through the lock's normal (non-reentrant) path.
      if (event === 'SIGNED_IN' && session) setTimeout(() => { hydrateProfile(session); }, 0);
    });

    // Re-hydrate when page is restored from bfcache (browser back from Stripe Checkout).
    // Without this, stale JS state persists and membership/subscription never re-fetch,
    // so the Subscribe button disappears after returning from Stripe.
    const handlePageShow = async (e) => {
      if (!e.persisted) return;
      const { data: { session: s }, error } = await supabase.auth.getSession();
      if (error) { setHydrationError(error.message); return; }
      if (s) await hydrateProfile(s);
    };
    window.addEventListener('pageshow', handlePageShow);

    // Handle Stripe redirect params
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      setBillingMsg('success');
      setView('billing');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('billing') === 'portal_return') {
      setView('billing');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('billing') === 'canceled') {
      window.history.replaceState({}, '', window.location.pathname);
    }

    return () => {
      authSub.unsubscribe();
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only; checkAuth/hydrateProfile only close over stable setState setters and the module-level supabase client

  // checkAuth: runs getSession() + hydrateProfile() with a 3s bound on how
  // long the initial spinner waits, WITHOUT treating "still in flight past
  // 3s" the same as "confirmed logged out" the way the old inline
  // Promise.race did. `settled` only flips once the real hydration chain
  // (success, no-session, or error) has actually finished — if the 3s
  // timer wins the race instead, that means we genuinely don't know the
  // outcome yet, so this shows a retryable error state rather than
  // silently falling through to LoginScreen. The underlying hydration
  // promise is never abandoned — if it finishes moments later, its own
  // setProfile/setHydrationError calls still land and correct the UI
  // without the user needing to click Retry.
  const checkAuth = async () => {
    let settled = false;
    const hydration = supabase.auth.getSession()
      .then(async ({ data: { session: s }, error: sessionErr }) => {
        if (sessionErr) { setHydrationError(sessionErr.message); return; }
        if (s) await hydrateProfile(s);
      })
      .catch((err) => setHydrationError(err?.message || 'Unknown error'))
      .finally(() => { settled = true; });

    const timeout = new Promise(resolve => setTimeout(resolve, 3000));
    await Promise.race([hydration, timeout]);
    setAuthChecking(false);
    if (!settled) setHydrationError('timeout');
  };

  const hydrateProfile = async (s) => {
    try {
      const { data: prof, error: profErr } = await supabase
        .from('profiles').select('id, first_name, last_name, weight_unit_pref, language_pref, is_superadmin, default_condition_filters').eq('auth_user_id', s.user.id).single();
      if (profErr) {
        // PGRST116 = .single() found zero rows — a real, expected "no
        // profile for this auth user" outcome. Previously fell through to
        // LoginScreen exactly like the old `if (!prof) return` did — a
        // valid session with no profile just looked logged-out, with no
        // explanation. Self-serve signup (2026-09-10) means this is now a
        // real, expected state on the normal signup path (a session
        // exists the moment supabase.auth.signUp() returns one, before
        // ensure_current_user_profile has ever run) — route to
        // CompleteClinicSignupScreen instead. Any OTHER error code is a
        // genuine query failure (network, RLS, timeout, 5xx) and must not
        // look the same.
        if (profErr.code === 'PGRST116') setNeedsProfileSetup(true);
        else setHydrationError(profErr.message);
        return;
      }
      if (!prof) return;
      const { data: member, error: memberErr } = await supabase
        .from('clinic_members')
        .select('clinic_id, is_doctor, is_manager, clinical_attestation_at, clinical_attestation_version, clinics(id, name, billing_program, enrollment_code, phone, emergency_hospital_1_name, emergency_hospital_1_phone, emergency_hospital_2_name, emergency_hospital_2_phone)')
        .eq('profile_id', prof.id)
        .eq('is_active', true)
        .maybeSingle();
      if (memberErr) { setHydrationError(memberErr.message); return; }
      if (!member?.clinic_id || !member.clinics) {
        // No active membership — check for a still-pending invite (same
        // query LoginScreen.handleSubmit already uses) before giving up.
        // See hydratedPendingInvite's declaration above for the full
        // rationale.
        const { data: pending, error: pendingErr } = await supabase
          .from('clinic_members')
          .select('clinic_id, is_doctor, is_manager, clinics(name)')
          .eq('profile_id', prof.id)
          .eq('is_active', false)
          .is('accepted_at', null)
          .maybeSingle();
        if (pendingErr) { setHydrationError(pendingErr.message); return; }
        if (pending?.clinic_id) {
          setHydrationError(null);
          setHydratedPendingInvite({ clinicId: pending.clinic_id, clinicName: pending.clinics?.name, profile: prof, membership: pending });
        }
        return;
      }
      setHydratedPendingInvite(null);
      setHydrationError(null);
      if (prof?.language_pref) i18n.changeLanguage(prof.language_pref);
      await requireMfaOrCommit({
        profile: prof,
        clinic: member.clinics,
        membership: member,
      });
    } catch (err) {
      console.error(err);
      setHydrationError(err?.message || 'Unknown error');
    }
  };

  // Fetches subscription on login and whenever the user opens the Billing tab.
  // 2026-08-27 (client-quality audit) — previously read only `{ data }`, so a
  // query error resolved with data=undefined/null and got treated identically
  // to "confirmed no subscription" (setSubscriptionLoaded(true) fired either
  // way). A Supabase query error is a resolved value here, not a rejection —
  // the old `.catch(() => {})` never even ran for it. Fixed to leave
  // subscriptionLoaded false on error instead of falsely marking it loaded:
  // BillingView's own `if (!subscriptionLoaded) return <Loading/>` (existing
  // code, unchanged) means an error now shows a stuck loading state rather
  // than a false "not subscribed," and since `subscription` stays null, the
  // `view !== 'billing' && subscription !== null` guard above naturally
  // retries the fetch the next time the user opens the Billing tab. The
  // explicit subscriptionError state also gives the current tab a retry
  // action instead of leaving it on an indefinite loading spinner.
  useEffect(() => {
    if (!clinic?.id) { setSubscription(null); setSubscriptionLoaded(false); setSubscriptionError(''); return; }
    if (view !== 'billing' && subscription !== null) return;
    setSubscriptionError('');
    supabase
      .rpc('get_clinic_subscription_status', { p_clinic_id: clinic.id })
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.warn('Failed to load clinic_subscriptions:', error.message);
          setSubscriptionError(error.message);
          return;
        }
        setSubscription(data || null);
        setSubscriptionLoaded(true);
        setSubscriptionError('');
      })
      .catch((err) => {
        console.warn('Failed to load clinic_subscriptions:', err?.message);
        setSubscriptionError(err?.message || 'Unknown error');
      });
  }, [clinic?.id, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshSubscription = async (clinicId) => {
    setSubscriptionError('');
    const { data, error } = await supabase
      .rpc('get_clinic_subscription_status', { p_clinic_id: clinicId })
      .maybeSingle();
    if (error) {
      console.warn('Failed to refresh clinic_subscriptions:', error.message);
      setSubscriptionError(error.message);
      return false;
    }
    setSubscription(data);
    setSubscriptionLoaded(true);
    setSubscriptionError('');
    return true;
  };

  // After a successful checkout redirect, poll until the webhook has activated the subscription.
  useEffect(() => {
    if (billingMsg !== 'success' || !clinic?.id) return;
    const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';
    if (isActive) return;
    const interval = setInterval(() => refreshSubscription(clinic.id), 2000);
    const timeout  = setTimeout(() => clearInterval(interval), 20000); // give up after 20s
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [billingMsg, clinic?.id, subscription?.status]);

  const commitLogin = ({ profile: p, clinic: cl, membership: m }) => {
    setProfile(p);
    setClinic(cl);
    setPendingMfaContext(null);
    // Harmless either way (the render gate is `!profile && ...`, and
    // profile is truthy the instant this runs), but avoids leaving a
    // stale reference around after a successful accept.
    setHydratedPendingInvite(null);
    if (m) setMembership({
      is_doctor: m.is_doctor ?? false,
      is_manager: m.is_manager ?? false,
      is_active: m.is_active !== false,
      clinical_attestation_at: m.clinical_attestation_at ?? null,
      clinical_attestation_version: m.clinical_attestation_version ?? null,
    });
  };

  const requireMfaOrCommit = async context => {
    // Scoped to superadmin only (Bryan's explicit correction, 2026-10-04):
    // requiring every clinic customer to install a third-party authenticator
    // app is an unacceptable adoption barrier and was never the intended
    // design. Regular clinic accounts (Manager/Doctor) skip the gate
    // entirely and commit immediately, regardless of AAL.
    if (!context.profile?.is_superadmin) {
      commitLogin(context);
      return;
    }
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) throw error;
    if (data?.currentLevel === 'aal2') {
      commitLogin(context);
      return;
    }
    setProfile(null); setClinic(null); setMembership(null);
    setPendingMfaContext(context);
  };

  const handleLogin = async context => requireMfaOrCommit(context);

  const handleMfaVerified = () => {
    if (pendingMfaContext) commitLogin(pendingMfaContext);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setProfile(null); setClinic(null); setMembership(null); setSubscription(null);
    setPendingMfaContext(null);
    setSubscriptionLoaded(false); setSubscriptionError('');
    setView('dashboard');
    workflowsLoadedRef.current = false;
    vetsLoadedRef.current = false;
    recoveryThresholdsLoadedRef.current = false;
    // _trackerCache is module-level and otherwise only cleared on a
    // freezeCutoff transition — on a shared workstation, the next staff
    // member to log in would otherwise be served this user's cached
    // per-pet tracker data until the tab reloads.
    Object.keys(_trackerCache).forEach(k => delete _trackerCache[k]);
    // filters was missing from this same shared-workstation reset (Phase 8
    // audit, 2026-07-15) — without this, a doctor's active clinician/
    // condition-type filter (whether picked manually or auto-applied from
    // their own default_condition_filters) silently carried into the next
    // person's session on the same machine, since App never remounts
    // across a logout→login cycle.
    setFilters({ sortBy: 'severity', clinician: '', conditionTypes: [] });
  };

  // Workforce offboarding must invalidate an already-open dashboard too,
  // not only future RLS requests. RLS is still the authoritative boundary;
  // this small focus/interval check removes cached/rendered patient state as
  // soon as the caller's own membership is no longer active.
  useEffect(() => {
    if (!profile?.id || !clinic?.id) return undefined;
    let stopped = false;
    const verifyMembership = async () => {
      const { data, error } = await supabase
        .from('clinic_members')
        .select('id')
        .eq('profile_id', profile.id)
        .eq('clinic_id', clinic.id)
        .eq('is_active', true)
        .maybeSingle();
      if (!stopped && !error && !data) {
        setPatients([]); setWellnessPatients([]); setPendingPets([]);
        setRejectedPets([]); setSelectedPatient(null);
        Object.keys(_trackerCache).forEach(k => delete _trackerCache[k]);
        await supabase.auth.signOut();
        if (!stopped) {
          setProfile(null); setClinic(null); setMembership(null);
          setSubscription(null); setSubscriptionLoaded(false);
        }
      }
    };
    const onFocus = () => verifyMembership();
    window.addEventListener('focus', onFocus);
    const timer = setInterval(verifyMembership, 60000);
    return () => {
      stopped = true;
      window.removeEventListener('focus', onFocus);
      clearInterval(timer);
    };
  }, [profile?.id, clinic?.id]);

  // Truly cross-tab data only: workflows/vets feed AssignModal and Sidebar
  // regardless of which tab is active, and pendingPets feeds Sidebar's
  // pending-count badge even while the user is sitting on Dashboard.
  // patients/wellnessPatients/rejectedPets are deliberately NOT fetched
  // here — they're DashboardView/EnrollmentsView's own job (below). This
  // used to also fetch patients+wellnessPatients, which on initial load ran
  // CONCURRENTLY with DashboardView's own onMount fetch (Dashboard being
  // the default tab, its mount effect fires in the same tick as this one)
  // — two full paginated fetchPatients runs racing each other on every
  // single login, each writing into the same setPatients accumulator.
  // That's what was actually behind "Load Trackers spins forever on
  // load, fixed only by a refresh" (2026-08-13 report): not a flaky
  // tracker fetch, but that fetch getting queued behind a doubled initial
  // request burst. Fixed by giving each dataset exactly one owner.
  const fetchGlobalData = useCallback(async () => {
    if (!clinic) return;
    await Promise.all([fetchPendingPets(clinic.id), fetchWorkflows(), fetchVets(clinic.id), fetchRecoveryThresholds()]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic]);

  useEffect(() => { if (clinic) fetchGlobalData(); }, [clinic, fetchGlobalData]);

  // Dashboard/Enrollments were previously inline `view === 'x' && <JSX>}`
  // blocks directly in App() — since that content never unmounted across
  // tab switches, patients/wellnessPatients/pendingPets/rejectedPets only
  // ever reflected one fetch at login, until either a full page reload or
  // the manual Refresh button (2026-08-10 report: "have to refresh to do
  // bloody anything"). Now real components (see dashboard/views/
  // DashboardView.js, EnrollmentsView.js) that App() mounts only while
  // their tab is active — each fires this on mount, so switching back to a
  // tab refetches fresh data for free, matching what SupportView already
  // did correctly. Data ownership deliberately stays here rather than
  // moving into those components: patients feeds Sidebar's nav and
  // PatientDetailPanel's onPrintSummary/optimistic dismiss-reassign
  // handlers below, and pendingPets feeds Sidebar's pending-count badge —
  // all of which render outside either view's own lifecycle.
  const handleDashboardMount = useCallback(async () => {
    if (!clinic?.id) return;
    if (dashboardRefreshRef.current) return dashboardRefreshRef.current;
    const refresh = (async () => {
      setLoadingDashboard(true);
      try {
        const results = await Promise.all([fetchPatients(clinic.id), fetchWellnessPatients(clinic.id)]);
        if (results.every(result => result !== false)) setLastUpdated(new Date());
      } finally {
        setLoadingDashboard(false);
        dashboardRefreshRef.current = null;
      }
    })();
    dashboardRefreshRef.current = refresh;
    return refresh;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic?.id]);

  const handleEnrollmentsMount = useCallback(async () => {
    if (!clinic?.id) return;
    if (enrollmentsRefreshRef.current) return enrollmentsRefreshRef.current;
    const refresh = (async () => {
      setLoadingEnrollments(true);
      try {
        await Promise.all([fetchPendingPets(clinic.id), fetchRejectedPets(clinic.id)]);
      } finally {
        setLoadingEnrollments(false);
        enrollmentsRefreshRef.current = null;
      }
    })();
    enrollmentsRefreshRef.current = refresh;
    return refresh;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic?.id]);

  // A clinician commonly leaves the dashboard open while seeing patients.
  // Refresh the active view when they return to the tab or regain network
  // connectivity, so new submissions/enrollments appear without a full page
  // reload. The in-flight guards above deduplicate focus+online events and a
  // simultaneous manual refresh into one request batch.
  useEffect(() => {
    if (!profile || !clinic?.id) return undefined;
    const refreshActiveView = () => {
      if (document.visibilityState === 'hidden') return;
      if (view === 'dashboard') handleDashboardMount();
      else if (view === 'enrollments') handleEnrollmentsMount();
    };
    window.addEventListener('focus', refreshActiveView);
    window.addEventListener('online', refreshActiveView);
    return () => {
      window.removeEventListener('focus', refreshActiveView);
      window.removeEventListener('online', refreshActiveView);
    };
  }, [profile, clinic?.id, view, handleDashboardMount, handleEnrollmentsMount]);

  // Shared with the past-due banner's own "X days left" countdown, which
  // previously computed the same 14-day boundary independently via
  // Math.floor(ms / 86400000) — calendar-day math (setDate) and raw
  // millisecond math aren't equivalent across a DST transition (a calendar
  // day spanning "spring forward" is only 23 real hours), so the two could
  // disagree by up to ~1 hour right at the boundary. Computing this once
  // and having both the banner and freezeCutoff derive from the same Date
  // makes that impossible by construction, not just unlikely.
  const pastDueGraceEnd = useMemo(() => {
    if (!subscription?.past_due_since) return null;
    const graceEnd = new Date(subscription.past_due_since);
    graceEnd.setDate(graceEnd.getDate() + 14);
    return graceEnd;
  }, [subscription?.past_due_since]);

  // Catch-up window (Audit §18.3) — historical record of the most recent
  // freeze's actual boundaries (stripe-webhook, migration 73), completely
  // separate from pastDueGraceEnd/freezeCutoff above. Only shown for 14
  // days after recovery so the "backlog" tag doesn't linger indefinitely.
  const catchUpWindow = useMemo(() => {
    if (!subscription?.last_freeze_started_at || !subscription?.last_freeze_ended_at) return null;
    const endedAt = new Date(subscription.last_freeze_ended_at);
    const visibleUntil = new Date(endedAt);
    visibleUntil.setDate(visibleUntil.getDate() + 14);
    if (new Date() > visibleUntil) return null;
    return { start: new Date(subscription.last_freeze_started_at), end: endedAt };
  }, [subscription?.last_freeze_started_at, subscription?.last_freeze_ended_at]);

  const isInCatchUpWindow = (dateStr) => {
    if (!catchUpWindow || !dateStr) return false;
    const d = new Date(dateStr);
    return d >= catchUpWindow.start && d <= catchUpWindow.end;
  };

  // Once the 14-day past-due grace period has actually elapsed, the
  // dashboard stops surfacing anything dated after the instant the grace
  // period ended — previously collected data stays fully visible, nothing
  // is deleted (data collection itself is unaffected — this is a read-side
  // filter only), and restoring the subscription clears past_due_since,
  // which makes this null again and instantly unfreezes everything. null
  // while healthy, still within grace, or on the beta billing program —
  // only set once truly lapsed.
  const freezeCutoff = useMemo(() => {
    if (clinic?.billing_program === 'beta') return null;
    if (!pastDueGraceEnd) return null;
    if (new Date() < pastDueGraceEnd) return null;
    return pastDueGraceEnd;
  }, [clinic?.billing_program, pastDueGraceEnd]);

  // PatientDetailPanel's module-level _trackerCache is keyed by pet id and
  // shared across every mount — its own read/write guards already bypass
  // it entirely while freezeCutoff is truthy (entering a freeze always
  // fetches fresh). But nothing previously cleared it on the reverse
  // transition: a clinic recovering from a lapse would have every already-
  // cached pet keep serving whatever was fetched before recovery, for the
  // rest of the browser session, since the guards only check "is frozen
  // right now," not "did the freeze state change since this was cached."
  // Cleared clinic-wide (not per-pet) since a freeze/unfreeze is a
  // clinic-wide event affecting every cached pet at once, not just
  // whichever one happens to be open.
  useEffect(() => {
    Object.keys(_trackerCache).forEach(k => delete _trackerCache[k]);
  }, [freezeCutoff]);

  const fetchPatients = async (clinicId) => {
    // Paginate through pet_clinic_relationships 200 at a time.
    // This keeps all downstream .in() clauses well under URL-length limits.
    // The completed refresh is committed once, after all pages succeed, so a
    // later-page failure cannot replace known-good state with a partial roster.
    //
    // Start from pet_clinic_relationships so we can show ALL conditions for each pet,
    // not just conditions where this clinic is the monitoring clinic (conditions.clinic_id).
    const PAGE_SIZE = 200;
    const oneYearAgo = new Date(); oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    setPatientsError(false);
    let accumulated = [];
    let offset = 0;

    // Derived auto-mute (migration 184, phase 3) — the 7-12mo inactivity
    // window is never stored, only computed at read time from completed
    // survey activity, so new activity un-mutes a pet with no clearing
    // logic anywhere. One clinic-scoped call, not per-page/per-pet.
    // Kicked off here but only awaited alongside the first page fetch
    // below (Promise.all) — it only needs clinicId, not petIds, so it has
    // no real dependency on pet_clinic_relationships and was previously
    // just wasting a full round trip in serial ahead of it for no reason.
    const derivedInactivePromise = supabase
      .rpc('get_derived_inactive_pet_ids', { p_clinic_id: clinicId });
    let derivedInactiveSet = null;

    while (true) {
      const relsPromise = supabase
        .from('pet_clinic_relationships')
        .select('pet_id, created_at, muted_at, muted_reason, pets(id, name, species, breed, date_of_birth, photo_url, owner_profile_id)')
        .eq('clinic_id', clinicId)
        .eq('is_active', true)
        .range(offset, offset + PAGE_SIZE - 1);

      let rels, relErr;
      if (derivedInactiveSet === null) {
        const [derivedResult, relsResult] = await Promise.all([derivedInactivePromise, relsPromise]);
        if (derivedResult.error) { setPatientsError(true); return false; }
        derivedInactiveSet = new Set((derivedResult.data || []).map(r => r.pet_id));
        ({ data: rels, error: relErr } = relsResult);
      } else {
        ({ data: rels, error: relErr } = await relsPromise);
      }

      if (relErr) { setPatientsError(true); return false; }

      if (!rels?.length) {
        break;
      }
      // Local snapshot for the closure below — derivedInactiveSet itself
      // is only ever reassigned once (on the very first iteration, above,
      // before any closure over it exists yet), but it's still a `let`
      // reassigned in this loop, which no-loop-func flags on the map
      // callback further down regardless.
      const derivedInactiveSetForPage = derivedInactiveSet;
      const petIds = rels.map(r => r.pet_id);
      const pets   = [...new Map(rels.filter(r => r.pets).map(r => [r.pet_id, r.pets])).values()];
      // pet_clinic_relationships.created_at — when this pet actually linked
      // to the clinic, distinct from any one condition's own created_at
      // (a pet can pick up new conditions long after first enrolling).
      // Previously fetched nowhere at all, so PatientDetailPanel had no
      // enrolled-date field to show regardless of what the panel itself did.
      const enrolledMap = Object.fromEntries(rels.map(r => [r.pet_id, r.created_at]));
      // muted_at (migration 183, phase 2 of the finalization redesign) —
      // a clinic-muted relationship stays active (owner unaffected, alerts
      // still generated) but is pulled out of the main severity groups
      // into its own collapsed "Inactive" section below, rather than
      // demanding clinician attention it explicitly opted out of.
      // manuallyMuted drives the Mute/Unmute action itself (only a real
      // clinic-initiated mute can be unmuted); muted (manual OR derived)
      // drives which section the card lands in — see fetchPatients'
      // derivedInactiveSet above.
      const manuallyMutedMap = Object.fromEntries(rels.map(r => [r.pet_id, !!r.muted_at]));
      const mutedReasonMap = Object.fromEntries(rels.map(r => [r.pet_id, r.muted_reason]));

      // Excludes general_health explicitly (2026-07-24) — this query had no
      // condition_type filter at all, so any pet's owner-side General
      // Health condition (is_active=true, clinic_id=null) would already
      // leak into this clinic's disease red/yellow/green groups as its own
      // "patient card" entry, pre-existing latent behavior surfaced while
      // building the separate Wellness dashboard section (which the spec
      // explicitly requires never mixes with disease-workflow patients).
      // General Health never has an alert/severity of its own (it's
      // completely bailed out of that pipeline server-side), so any such
      // entry would default to a meaningless "green" card with no real
      // clinical content — this fix has no effect on any real disease
      // condition, only removes a spurious duplicate.
      const { data: conditions, error: condErr } = await supabase
        .from('conditions')
        .select('id, condition_type, status, pet_id, assigned_by, clinic_id, clinical_summary')
        .in('pet_id', petIds)
        .eq('is_active', true)
        .neq('condition_type', 'general_health');

      if (condErr) { setPatientsError(true); return false; }

      const pageConditions = conditions || [];
      const conditionIds   = pageConditions.map(c => c.id);

      const [alertsRes, surveysRes] = await Promise.all([
        // Fetch active alert narratives for ALL visible conditions — not just this clinic's.
        // The narrative is part of the condition's clinical picture; dismiss is gated
        // at write time by the monitoring clinic check in the mutation handlers.
        // freezeCutoff (past-due grace period elapsed) hides alerts/surveys created after
        // the freeze instant — previously collected data stays visible, nothing is deleted.
        // acknowledged_at/acknowledged_attention_level/snoozed_until are no longer
        // selected — Dismiss/Snooze/Acknowledge merged into one "Mark reviewed"
        // action (2026-09-09), and nothing client-side reads them anymore (the
        // Immediate Review step-down gate they drive is entirely server-side).
        conditionIds.length
          ? (() => {
              let q = supabase.from('alerts').select('id, pet_id, condition_id, severity, current_severity, message, is_resolved, created_at, recovery_streak, dismissed_at, dismissed_by').in('condition_id', conditionIds).eq('is_resolved', false);
              if (freezeCutoff) q = q.lte('created_at', freezeCutoff.toISOString());
              return q;
            })()
          : Promise.resolve({ data: [], error: null }),
        // Cap surveys to the last year — we only use the most recent one per condition
        // (surveys.find), so pulling all history wastes bandwidth at scale.
        (() => {
          let q = supabase.from('survey_submissions').select('id, pet_id, condition_id, clinical_score, clinical_score_kind, clinical_score_unit, clinical_score_delta, submitted_at').in('pet_id', petIds).eq('status', 'completed').gte('submitted_at', oneYearAgo.toISOString()).order('submitted_at', { ascending: false });
          if (freezeCutoff) q = q.lte('submitted_at', freezeCutoff.toISOString());
          return q;
        })(),
      ]);

      // Alert severity is the dashboard's primary clinical signal. A failed
      // alert read must never be converted into an empty array and therefore a
      // green patient. Keep the last fully confirmed roster on screen and show
      // the retryable error state instead. Survey failure is handled the same
      // way so "last check-in" cannot silently disappear from a partial page.
      if (alertsRes.error || surveysRes.error) {
        console.warn('Clinical patient-segment read failed:', alertsRes.error?.message || surveysRes.error?.message);
        setPatientsError(true);
        return false;
      }

      const alerts  = alertsRes.data  || [];
      const surveys = surveysRes.data || [];

      // vNext findings-based "why" narrative (release-blocker #2,
      // docs/BUILD_STATE.md) — survey_evaluation_runs is the vNext shadow
      // engine's append-only history (Stage 0A, migration 260), keyed by
      // survey_submission_id. Only the most-recent survey per condition
      // (surveys.find below) is ever displayed on the roster, so this only
      // needs those submissions' own runs, not full history. Best-effort:
      // a failed or empty read here should never block the roster itself
      // from rendering — StatusNarrative falls back to clinicalSummary/
      // alert.message when no vNext run exists for a submission (most
      // conditions/pets, until vNext is live-tested at real scale).
      const lastSurveyIds = surveys.map(s => s.id).filter(Boolean);
      let vnextRunsBySubmissionId = new Map();
      if (lastSurveyIds.length > 0) {
        const { data: vnextRuns, error: vnextErr } = await supabase
          .from('survey_evaluation_runs')
          .select('survey_submission_id, findings, clusters')
          .in('survey_submission_id', lastSurveyIds)
          .eq('computation_kind', 'original');
        if (vnextErr) {
          console.warn('vNext findings read failed (non-fatal, narrative falls back):', vnextErr.message);
        } else {
          vnextRunsBySubmissionId = new Map((vnextRuns || []).map(r => [r.survey_submission_id, r]));
        }
      }

      const ownerIds      = [...new Set(pets.map(p => p.owner_profile_id).filter(Boolean))];
      const vetIds        = [...new Set(pageConditions.map(c => c.assigned_by).filter(Boolean))];
      const allProfileIds = [...new Set([...ownerIds, ...vetIds])];
      // get_owner_contacts_for_clinic (migration 158) instead of a direct
      // .from('profiles') select — the RLS policy that used to let clinic
      // staff read another profile's row directly was removed (row-level
      // RLS can't restrict columns, so it exposed push_token/consent
      // timestamps/is_superadmin/etc. to any raw client, not just the
      // narrow column list this file happens to ask for). The RPC
      // re-checks the same clinic_staff_can_read_profile()/pending-
      // reviewer authorization and returns only the columns clinic
      // dashboards actually use.
      const { data: profilesData, error: profilesError } = await supabase.rpc('get_owner_contacts_for_clinic', { p_profile_ids: allProfileIds });
      if (profilesError) {
        console.warn('Patient contact read failed:', profilesError.message);
        setPatientsError(true);
        return false;
      }

      const pageBuilt = pageConditions.map(cond => {
        const pet         = pets.find(p => p.id === cond.pet_id);
        const owner       = profilesData?.find(o => o.id === pet?.owner_profile_id);
        const assignedVet = profilesData?.find(v => v.id === cond.assigned_by) || null;
        const alert       = alerts.find(a => a.condition_id === cond.id);
        const lastSurvey  = surveys.find(s => s.condition_id === cond.id);
        // Derive severity from the active alert rather than conditions.status.
        // conditions.status is set by the process-survey-alert webhook; if the webhook
        // fails or the secret is wrong, it stays stale. The alert row is written in the
        // same function but earlier, so it's more reliable. No active alert = stable.
        // immediate_review used to be silently collapsed into 'red' here —
        // the highest-acuity tier and the entire reason "emergency"
        // language was removed from the product had no distinct dashboard
        // treatment at all. Kept as its own severity now; see
        // severityColor/severityBg and the dedicated section below.
        const severity = alert?.severity || 'green';
        const manuallyMuted = manuallyMutedMap[cond.pet_id] || false;
        const vnextRun = lastSurvey ? vnextRunsBySubmissionId.get(lastSurvey.id) : null;
        return {
          pet, condition: cond, owner, alert, lastSurvey, vnextRun, severity, assignedVet,
          enrolledAt: enrolledMap[cond.pet_id] || null,
          manuallyMuted,
          muted: manuallyMuted || derivedInactiveSetForPage.has(cond.pet_id),
          mutedReason: mutedReasonMap[cond.pet_id] || null,
        };
      }).filter(p => p.pet);

      // CHF/Chronic Respiratory Disease and their companion SRR condition
      // are separate rows (separate trend charts, separate condition_id) —
      // deliberately kept that way. But an SRR alert (elevated breathing
      // rate) can fire while the parent survey itself still looks stable,
      // and a clinician looking at just that row has no way to know to
      // check SRR too. Flag the parent row whenever its sibling SRR
      // condition has an active (unresolved) alert — same-pet lookup, safe
      // within one page since pagination is by pet, so a pet's parent and
      // SRR rows always land in the same batch. A pet can only ever have
      // one SRR condition regardless of which parent triggered it (see
      // assign_workflow_to_pet's shared srr lookup), so this naturally
      // covers CHF+Chronic-Respiratory-Disease-together too.
      const srrAlertsByPet = {};
      pageBuilt.forEach(row => {
        if (row.condition?.condition_type === 'srr' && row.alert) {
          srrAlertsByPet[row.pet.id] = row.alert;
        }
      });
      pageBuilt.forEach(row => {
        if ((row.condition?.condition_type === 'chf' || row.condition?.condition_type === 'chronic_respiratory') && srrAlertsByPet[row.pet.id]) {
          row.linkedSignificantChange = srrAlertsByPet[row.pet.id];
        }
      });

      accumulated = [...accumulated, ...pageBuilt];

      if (rels.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    // Commit the refresh only after every page and every clinical segment has
    // succeeded. This prevents a later-page failure from replacing a known-good
    // roster with a partial one whose missing alerts look stable.
    setPatients(accumulated);
    return true;
  };

  // Wellness section (migration 104) — deliberately separate from
  // fetchPatients above, not a filter/branch inside it: this reads
  // relationship_type='wellness_linked' pets and their general_health
  // condition's compact current-state columns only (wellness_status/
  // summary/changed_domains, already computed and persisted by
  // recompute_wellness_status — no recalculation from full survey history
  // on every dashboard load, same "compact current-state" pattern this
  // week's architecture report recommends generally). A .limit(500)
  // defensive cap rather than full incremental pagination — this is a
  // collapsed, secondary section, not the primary hot path fetchPatients
  // is, and wellness-linked-without-a-workflow is a brand new relationship
  // type with no existing population to speak of yet.
  const fetchWellnessPatients = async (clinicId) => {
    setWellnessPatientsError(false);
    const { data: rels, error: relErr } = await supabase
      .from('pet_clinic_relationships')
      .select('pet_id, pets(id, name, species, owner_profile_id, is_active)')
      .eq('clinic_id', clinicId)
      .eq('relationship_type', 'wellness_linked')
      .eq('is_active', true)
      .limit(500);
    // A read failure here must never collapse to the same `[]` a
    // genuinely empty wellness roster renders as (WellnessSection
    // returns null either way) — that's the exact "failed read looks
    // like a quiet, healthy result" bug class the Reliability audit
    // flagged (2026-09-14). Keep the last confirmed roster on screen
    // (same pattern fetchPatients' alerts/surveys checks already use)
    // instead of silently blanking the whole section.
    if (relErr) {
      console.warn('Wellness relationship read failed:', relErr.message);
      setWellnessPatientsError(true);
      return;
    }
    if (!rels?.length) { setWellnessPatients([]); return; }

    // pet_clinic_relationships.is_active tracks the CLINIC LINK, not the
    // pet's own lifecycle — deactivating a pet (AddPetScreen/EditPetScreen,
    // "no longer with us"/etc.) does not cascade to flip this relationship
    // row, so a deactivated pet's wellness_linked row can stay is_active
    // indefinitely. fetchPatients above never hit this because it iterates
    // from `conditions` (already is_active-filtered) instead of `pets` —
    // this is the one place that reads pets directly, so it's the one
    // place that needs its own explicit check. Found live 2026-08-14:
    // a deactivated test pet ("Kelev") kept showing under Wellness on the
    // dashboard despite correctly disappearing from both the owner app and
    // the main disease-condition sections.
    const petIds = rels.map(r => r.pet_id);
    const pets   = [...new Map(rels.filter(r => r.pets && r.pets.is_active !== false).map(r => [r.pet_id, r.pets])).values()];

    const { data: conditions, error: condErr } = await supabase
      .from('conditions')
      .select('id, pet_id, wellness_status, wellness_summary, wellness_changed_domains, wellness_summary_generated_at')
      .in('pet_id', petIds)
      .eq('condition_type', 'general_health')
      .eq('is_active', true);
    // Unchecked before this fix: a failed read here left `conditions`
    // undefined, `cond` null for every pet, and wellnessStatus defaulting
    // to null -- WellnessSection's own ternary treats anything that isn't
    // literally 'yellow' as green, so this alone could have rendered a
    // solid green dot for every wellness patient on a failed read.
    if (condErr) {
      console.warn('Wellness condition read failed:', condErr.message);
      setWellnessPatientsError(true);
      return;
    }

    const conditionIds = (conditions || []).map(c => c.id);
    const { data: surveys, error: survErr } = conditionIds.length
      ? await supabase
          .from('survey_submissions')
          .select('condition_id, status, due_at, submitted_at')
          .in('condition_id', conditionIds)
          .order('due_at', { ascending: false })
      : { data: [], error: null };
    if (survErr) {
      console.warn('Wellness survey read failed:', survErr.message);
      setWellnessPatientsError(true);
      return;
    }

    const ownerIds = [...new Set(pets.map(p => p.owner_profile_id).filter(Boolean))];
    // See the migration-158 comment above (App.js's other profiles call
    // site) — same restricted RPC instead of a direct table read.
    const { data: profilesData, error: profilesErr } = ownerIds.length
      ? await supabase.rpc('get_owner_contacts_for_clinic', { p_profile_ids: ownerIds })
      : { data: [], error: null };
    if (profilesErr) {
      console.warn('Wellness owner contact read failed:', profilesErr.message);
      setWellnessPatientsError(true);
      return;
    }

    const built = pets.map(pet => {
      const cond = (conditions || []).find(c => c.pet_id === pet.id) || null;
      const owner = profilesData?.find(o => o.id === pet.owner_profile_id) || null;
      const condSurveys = cond ? (surveys || []).filter(s => s.condition_id === cond.id) : [];
      const lastCheckin = condSurveys.find(s => s.status === 'completed');
      const nextCheckin = [...condSurveys].reverse().find(s => s.status === 'scheduled');
      return {
        pet, owner, condition: cond,
        wellnessStatus: cond?.wellness_status || null,
        wellnessSummary: cond?.wellness_summary || null,
        lastCheckinAt: lastCheckin?.submitted_at || null,
        nextCheckinAt: nextCheckin?.due_at || null,
      };
    });

    setWellnessPatients(built);
  };

  // Owner contact was previously embedded directly (`profiles!owner_
  // profile_id(...)`) here. Migration 158 removed clinic staff's RLS read
  // access to another user's raw `profiles` row (it couldn't restrict
  // columns, so it also exposed push_token/consent timestamps/is_
  // superadmin to any client) and replaced it with the get_owner_
  // contacts_for_clinic RPC — fetchPatients/fetchWellnessPatients were
  // updated to call it, but these two Enrollments-tab fetches were missed.
  // An embedded PostgREST join blocked by RLS resolves to a silent null,
  // not an error, so this shipped as a blank owner name/contact ("—",
  // EnrollmentsView's own fallback) on every pending/rejected pet, with no
  // visible failure anywhere — confirmed live 2026-09-24, medical director
  // walkthrough. get_owner_contacts_for_clinic already covers this exact
  // case (its own SECURITY DEFINER check includes
  // is_pending_clinic_reviewer_for_pet specifically for pending review),
  // so no new RPC is needed, only wiring these two call sites to it.
  const fetchPendingPets = async (clinicId) => {
    setPendingError(false);
    const { data: rels, error } = await supabase
      .from('pet_clinic_relationships')
      .select(`
        id, pet_id, vcpr_attested_at,
        pets(id, name, species, breed, date_of_birth, photo_url, owner_profile_id)
      `)
      .eq('clinic_id', clinicId)
      .eq('relationship_type', 'pending')
      .eq('is_active', false);
    if (error) { console.error('fetchPendingPets failed:', error); setPendingError(true); setPendingPets([]); return; }
    if (!rels?.length) { setPendingPets([]); return; }
    const withPets = rels.filter(r => r.pets);
    const ownerIds = [...new Set(withPets.map(r => r.pets.owner_profile_id).filter(Boolean))];
    const { data: profilesData, error: profilesError } = ownerIds.length
      ? await supabase.rpc('get_owner_contacts_for_clinic', { p_profile_ids: ownerIds })
      : { data: [], error: null };
    if (profilesError) {
      console.error('fetchPendingPets owner contact read failed:', profilesError.message);
      setPendingError(true); setPendingPets([]); return;
    }
    setPendingPets(withPets.map(r => ({
      ...r.pets,
      relationshipId: r.id,
      vcprAttestedAt: r.vcpr_attested_at,
      profiles: profilesData?.find(o => o.id === r.pets.owner_profile_id) || null,
    })));
  };

  const fetchRejectedPets = async (clinicId) => {
    setRejectedError(false);
    const since = new Date();
    since.setDate(since.getDate() - 30);
    const { data: rels, error } = await supabase
      .from('pet_clinic_relationships')
      .select(`
        pet_id, updated_at,
        pets(id, name, species, breed, date_of_birth, owner_profile_id)
      `)
      .eq('clinic_id', clinicId)
      .eq('relationship_type', 'rejected')
      .gte('updated_at', since.toISOString());
    if (error) { console.error('fetchRejectedPets failed:', error); setRejectedError(true); setRejectedPets([]); return; }
    if (!rels?.length) { setRejectedPets([]); return; }
    const withPets = rels.filter(r => r.pets);
    const ownerIds = [...new Set(withPets.map(r => r.pets.owner_profile_id).filter(Boolean))];
    const { data: profilesData, error: profilesError } = ownerIds.length
      ? await supabase.rpc('get_owner_contacts_for_clinic', { p_profile_ids: ownerIds })
      : { data: [], error: null };
    if (profilesError) {
      console.error('fetchRejectedPets owner contact read failed:', profilesError.message);
      setRejectedError(true); setRejectedPets([]); return;
    }
    setRejectedPets(withPets.map(r => ({
      ...r.pets,
      rejectedAt: r.updated_at,
      profiles: profilesData?.find(o => o.id === r.pets.owner_profile_id) || null,
    })));
  };

  const fetchWorkflows = async () => {
    if (workflowsLoadedRef.current) return;
    const { data, error } = await supabase.from('survey_templates')
      .select('id, name, condition_type')
      .eq('is_active', true)
      .order('name');
    if (error) {
      console.warn('Workflow list failed to load:', error.message);
      workflowsLoadedRef.current = false;
      return;
    }
    // SRR is a companion check attached automatically when CHF is assigned
    // (see handleAssign) — it's never a standalone, manually-assignable workflow.
    setWorkflows((data || []).filter(w =>
      w.condition_type !== 'srr'
      && w.condition_type !== 'general_health'
      && !w.condition_type?.startsWith('alpha_')
    ));
    workflowsLoadedRef.current = true;
  };

  // #9: one row per active condition_type (unique (condition_type, version)
  // plus is_active, migration 105) — small global config table, safe to
  // fetch in full rather than per-pet. Defaults to 2 for a null column,
  // matching apply_survey_processing_result_unordered's own
  // coalesce(..., 2) so the dashboard's displayed threshold can never
  // drift from what the backend actually gates on.
  const fetchRecoveryThresholds = async () => {
    if (recoveryThresholdsLoadedRef.current) return;
    const { data, error } = await supabase.from('condition_alert_profiles')
      .select('condition_type, yellow_recovery_count, red_recovery_count, immediate_review_recovery_count')
      .eq('is_active', true);
    if (error) {
      console.warn('Recovery threshold list failed to load:', error.message);
      recoveryThresholdsLoadedRef.current = false;
      return;
    }
    const map = {};
    (data || []).forEach(p => {
      map[p.condition_type] = {
        yellow: p.yellow_recovery_count ?? 2,
        red: p.red_recovery_count ?? 2,
        immediate_review: p.immediate_review_recovery_count ?? 2,
      };
    });
    setRecoveryThresholds(map);
    recoveryThresholdsLoadedRef.current = true;
  };

  const fetchVets = async (clinicId) => {
    if (vetsLoadedRef.current) return;
    // Same ambiguous-embed issue as PermissionsView's staff list (see its
    // comment) — clinic_members has two FKs to profiles, so this must be
    // disambiguated too, or the query is silently rejected and this list
    // (the doctor picker in AssignModal) is just permanently empty with no
    // visible error at all, since the caller never checked `error` here.
    const { data, error } = await supabase
      .from('clinic_members')
      .select('profile_id, profiles!clinic_members_profile_id_fkey(id, first_name, last_name)')
      .eq('clinic_id', clinicId)
      .eq('is_active', true)
      .eq('is_doctor', true);
    if (error) { console.error('fetchVets error:', error); return; }
    const vetsData = (data || []).map(m => m.profiles).filter(Boolean);
    setVets(vetsData.sort((a, b) => a.id === profile?.id ? -1 : 1));
    vetsLoadedRef.current = true;
  };

  const handlePrintSummary = async (patient) => {
    const petId  = patient.pet?.id;
    if (!petId) return;
    const since12m     = new Date(); since12m.setFullYear(since12m.getFullYear() - 1);
    const sinceISO     = since12m.toISOString();
    const sinceDate    = sinceISO.split('T')[0];

    const petPatients = patients.filter(p => p.pet?.id === petId);
    const allConditions = petPatients.map(p => p.condition).filter(Boolean);

    // Same alert-derived severity the dashboard badge uses (Session 32) — not
    // conditions.status, which is written later in the webhook flow and can
    // legitimately disagree with it. Previously the print summary read
    // conditions.status directly, so the PDF and the live dashboard could
    // show two different severities for the same patient.
    const condSeverity = {};
    petPatients.forEach(p => { if (p.condition) condSeverity[p.condition.id] = p.severity; });

    const conditionIds = allConditions.map(c => c.id);

    // freezeCutoff (past-due grace period elapsed) hides anything dated
    // after the freeze instant in the printed summary too — previously
    // collected data stays visible, medication_entries stays unfiltered
    // (structural, current med list), same split as the live dashboard.
    let weightsQ   = supabase.from('weight_entries').select('weight,unit,measured_at').eq('pet_id', petId).gte('measured_at', sinceDate).is('deleted_at', null).order('measured_at', { ascending: true });
    let goodDaysQ  = supabase.from('good_day_logs').select('log_date,value').eq('pet_id', petId).gte('log_date', sinceDate).is('deleted_at', null).order('log_date', { ascending: true });
    let seizuresQ  = supabase.from('seizure_logs').select('occurred_at,duration_bucket,cluster,recovery,note').eq('pet_id', petId).gte('occurred_at', sinceISO).is('deleted_at', null).order('occurred_at', { ascending: false });
    let eventsQ    = supabase.from('intervention_events').select('event_type,event_date,notes').eq('pet_id', petId).gte('event_date', sinceDate).is('deleted_at', null).order('event_date', { ascending: false });
    // Merged with intervention_events below — same reasoning as the live
    // dashboard's equivalent fetch (App.js's interventionEvents effect):
    // medication_change_reasons now carries what medication_started/
    // medication_stopped/dose_changed used to be as manual intervention_
    // events entries, same display shape.
    let medChangesQ = supabase.from('medication_change_reasons').select('change_type,recorded_at,reason').eq('pet_id', petId).gte('recorded_at', sinceISO).is('deleted_at', null).order('recorded_at', { ascending: false });
    let surveysQ   = conditionIds.length
      ? supabase.from('survey_submissions').select('id,condition_id,clinical_score,clinical_score_kind,clinical_score_unit,clinical_score_delta,submitted_at').in('condition_id', conditionIds).eq('status', 'completed').gte('submitted_at', sinceISO).order('submitted_at', { ascending: true })
      : Promise.resolve({ data: [] });
    // Phase 7: only the most recent check-in's reason gets printed (see
    // buildTrendSvg's own comment) — a static PDF can't offer hover, so
    // rather than trying to annotate every historical point, the single
    // clinically relevant one for a point-in-time summary is "what's the
    // reason behind the current state," matching what the summary already
    // leads with (latest score/date).
    let evaluationsQ = conditionIds.length
      ? supabase.from('survey_evaluations').select('survey_submission_id,severity_band,change_state,primary_reason_code,reasons').in('condition_id', conditionIds)
      : Promise.resolve({ data: [] });
    // vNext findings-based "why" narrative for the printed summary too
    // (release-blocker #2) — same append-only history table as the live
    // dashboard's PatientDetailPanel/TrendChart, filtered the same way.
    let vnextRunsQ = conditionIds.length
      ? supabase.from('survey_evaluation_runs').select('survey_submission_id,findings,clusters').in('condition_id', conditionIds).eq('computation_kind', 'original')
      : Promise.resolve({ data: [] });
    let adherenceQ = supabase.from('adherence_entries').select('medication_entry_id').eq('pet_id', petId).gte('recorded_at', sinceISO).is('deleted_at', null);
    if (freezeCutoff) {
      const cutoffISO = freezeCutoff.toISOString();
      weightsQ  = weightsQ.lte('measured_at', cutoffISO);
      goodDaysQ = goodDaysQ.lte('log_date', cutoffISO);
      seizuresQ = seizuresQ.lte('occurred_at', cutoffISO);
      eventsQ   = eventsQ.lte('event_date', cutoffISO);
      medChangesQ = medChangesQ.lte('recorded_at', cutoffISO);
      if (conditionIds.length) surveysQ = surveysQ.lte('submitted_at', cutoffISO);
      adherenceQ = adherenceQ.lte('recorded_at', cutoffISO);
      // Found in the Phase 7 RLS audit: this was the one query in this
      // function not getting the freeze-cutoff filter its siblings all
      // get above — a defense-in-depth client-side fix alongside
      // migration 130's RLS fix (the actual backstop), not a substitute
      // for it. created_at matches migration 130's own column choice.
      if (conditionIds.length) evaluationsQ = evaluationsQ.lte('created_at', cutoffISO);
      if (conditionIds.length) vnextRunsQ = vnextRunsQ.lte('created_at', cutoffISO);
    }

    const printResults = await Promise.all([
      supabase.from('medication_entries').select('id,medication_name,dose,dose_unit,frequency,start_date').eq('pet_id', petId).eq('is_active', true).order('start_date', { ascending: false }),
      weightsQ,
      goodDaysQ,
      seizuresQ,
      eventsQ,
      medChangesQ,
      surveysQ,
      // adherence_entries: written only from the mobile app's "Mark as Given"
      // push-notification action — previously collected but never read
      // anywhere, including here. A confirmed-dose count over the print
      // window is a modest but real adherence signal worth surfacing.
      adherenceQ,
      evaluationsQ,
      vnextRunsQ,
    ]);
    // None of these checked `.error` before this fix -- a failed read
    // (RLS denial, transient failure) silently produced an empty section
    // in the printed PDF, indistinguishable from "no data recorded" for
    // that category. A clinician or owner reading the summary has no way
    // to tell the difference (Reliability audit, 2026-09-14: "print/
    // export failures must be explicit... required clinical reads must
    // fail the export"). Abort the whole export rather than hand out a
    // PDF that looks complete but silently omitted something.
    const firstError = printResults.find(r => r.error)?.error;
    if (firstError) {
      console.warn('Print summary data fetch failed:', firstError.message);
      window.alert(t('mainView.printSummaryFailed'));
      return;
    }
    const [
      { data: medications },
      { data: weights },
      { data: goodDays },
      { data: seizures },
      { data: eventsRaw },
      { data: medChangesRaw },
      { data: allSurveyData },
      { data: adherence },
      { data: allEvaluationData },
      { data: allVnextRunData },
    ] = printResults;
    const evaluationBySurveyId = new Map((allEvaluationData || []).map(e => [e.survey_submission_id, e]));
    const vnextRunBySurveyId = new Map((allVnextRunData || []).map(r => [r.survey_submission_id, r]));

    const events = [
      ...(eventsRaw || []),
      ...(medChangesRaw || []).map(m => ({
        event_type: m.change_type,
        event_date: (m.recorded_at || '').slice(0, 10),
        notes:      m.reason,
      })),
    ].sort((a, b) => (a.event_date < b.event_date ? 1 : a.event_date > b.event_date ? -1 : 0));

    const adherenceCounts = {};
    (adherence || []).forEach(a => {
      adherenceCounts[a.medication_entry_id] = (adherenceCounts[a.medication_entry_id] || 0) + 1;
    });

    const condSurveys = {};
    allConditions.forEach(c => { condSurveys[c.id] = []; });
    (allSurveyData || []).forEach(s => { if (condSurveys[s.condition_id]) condSurveys[s.condition_id].push(s); });

    // Defensive fallback, matching the `conditions` fallback below
    if (patient.condition && condSeverity[patient.condition.id] === undefined) {
      condSeverity[patient.condition.id] = patient.severity;
    }

    const html = buildPrintHtml({
      pet:        patient.pet,
      owner:      patient.owner,
      conditions: allConditions.length ? allConditions : [patient.condition].filter(Boolean),
      condSeverity,
      condSurveys,
      evaluationBySurveyId,
      vnextRunBySurveyId,
      medications: medications || [],
      adherenceCounts,
      weights:     weights    || [],
      goodDays:    goodDays   || [],
      seizures:    seizures   || [],
      events:      events     || [],
      useLbs,
    });

    const win = window.open('', '_blank', 'width=820,height=960');
    if (!win) { alert(t('mainView.printPopupBlocked')); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);
  };

  // Dismiss/snooze/reassign previously each did a full fetchPatients()
  // refetch — a complete re-run of the paginated pet_clinic_relationships →
  // conditions → alerts+surveys → profiles chain for the entire roster, to
  // reflect a single-row change. Targeted local state updates instead;
  // matches the optimistic pattern these already used for selectedPatient,
  // just extended to the main patients array so the roster (and the tab a
  // pet lands in) updates immediately without waiting on or paying for a
  // full roster re-fetch.

  // Mutations themselves live in dashboard/alertActions.js (unit-testable
  // in isolation). Previously handleReassignDoctor discarded the update's
  // result entirely — if it failed for any reason (RLS, a stale/expired
  // session, a network blip), the local state below still optimistically
  // applied as if it had succeeded, so it LOOKED reassigned until the
  // next reload/refetch, when the real server-side state would just
  // reappear. handleDismissAlert already had this fix (2026-08-05,
  // "Dismiss doesn't work" report); extended here (2026-08-16 audit)
  // rather than just Dismiss.
  // Corrected 2026-09-08 (Bryan, real usage): Dismiss previously cleared
  // the alert entirely via clearAlertAndLinkedFlag, forcing severity to
  // 'green' — that dropped patients into the undifferentiated Green —
  // Stable bucket with no way back short of a new survey. Dismiss now
  // means "reviewed, out of the active queue until the next survey" — a
  // queue-position patch only. severity/current_severity/color are left
  // completely untouched; groupPatients' section-placement logic (below)
  // is what routes a dismissed alert to Recently Reviewed instead of its
  // natural severity section.

  // Merged 2026-09-09 (Bryan): Dismiss, Snooze, and Acknowledge all did
  // the same underlying thing — move an alert out of the active queue —
  // with only cosmetic differences in when it resurfaces. Collapsed into
  // one "Mark reviewed" action. dismiss_alert() (migration 209) now also
  // performs Acknowledge's old Immediate Review step-down release when
  // reviewing an alert whose recovery streak already met threshold —
  // steppedDown here is exactly the old handleAcknowledgeAlert's patch,
  // just applied from this single handler instead of a second button.
  const handleDismissAlert = async (alertId) => {
    const { success, steppedDown } = await dismissAlertMutation(alertId, profile?.id);
    if (!success) {
      window.alert(t('mainView.dismissFailed'));
      return;
    }
    const nowIso = new Date().toISOString();
    const patch = p => {
      if (p.alert?.id !== alertId) return p;
      const nextSeverity = steppedDown ? 'red' : p.alert.severity;
      return {
        ...p,
        severity: nextSeverity,
        alert: {
          ...p.alert,
          dismissed_at: nowIso,
          dismissed_by: profile?.id ?? null,
          severity: nextSeverity,
          recovery_streak: steppedDown ? 0 : p.alert.recovery_streak,
        },
      };
    };
    setPatients(prev => prev.map(patch));
    setSelectedPatient(prev => (prev ? patch(prev) : prev));
  };

  const handleReactivateAlert = async (alertId) => {
    const { success, companionAlertId } = await reactivateAlertMutation(alertId);
    if (!success) {
      window.alert(t('mainView.reactivateFailed'));
      return;
    }
    // Corrected in review: reactivate_alert() (migration 206) also
    // reactivates a linked CHF/chronic_respiratory<->SRR companion alert
    // server-side when one exists and is dismissed. Without patching it
    // here too, the companion card would keep showing as dismissed until
    // the next full refetch — the same "it worked, but only after
    // refresh" failure mode this whole redesign has been fixing all
    // night, just for a second alert this time.
    const clearedFields = { dismissed_at: null, dismissed_by: null };
    const patchBoth = p => {
      if (p.alert?.id === alertId) return { ...p, alert: { ...p.alert, ...clearedFields } };
      if (companionAlertId && p.alert?.id === companionAlertId) return { ...p, alert: { ...p.alert, ...clearedFields } };
      return p;
    };
    setPatients(prev => prev.map(patchBoth));
    setSelectedPatient(prev => (prev ? patchBoth(prev) : prev));
  };

  const handleReassignDoctor = async (conditionId, vetId) => {
    const { success } = await reassignDoctorMutation(conditionId, vetId);
    if (!success) {
      window.alert(t('mainView.reassignFailed'));
      return;
    }
    const newVet = vets.find(v => v.id === vetId) || null;
    setPatients(prev => prev.map(p => p.condition?.id === conditionId ? { ...p, assignedVet: newVet } : p));
    setSelectedPatient(prev => prev ? { ...prev, assignedVet: newVet } : null);
  };

  const handleReject = async (pet) => {
    if (processingPetIds.has(pet.id)) return;
    setProcessingPetIds(prev => new Set(prev).add(pet.id));
    try {
      await handleRejectInner(pet);
    } finally {
      setProcessingPetIds(prev => { const next = new Set(prev); next.delete(pet.id); return next; });
    }
  };

  const handleRejectInner = async (pet) => {
    // Was two separate unguarded sequential updates (pet_clinic_relationships
    // then conditions) with no shared transaction — a failure between them
    // left a rejected relationship with a still-active condition, which a
    // clinician would see as a pet they just rejected still showing as an
    // active patient (architecture report finding, 2026-07-23, migration
    // 102). reject_pet_enrollment wraps both in one transaction and raises a
    // clear error if the caller isn't authorized instead of silently
    // no-op'ing (RLS blocks the update, not the RPC call itself).
    const { error: rejectErr } = await supabase.rpc('reject_pet_enrollment', {
      p_pet_id:    pet.id,
      p_clinic_id: clinic.id,
    });
    if (rejectErr) {
      console.warn('reject_pet_enrollment failed:', rejectErr);
      alert(t('mainView.rejectFailedGeneric'));
      return;
    }
    // Audit event now logged server-side inside reject_pet_enrollment
    // itself (migration 226) — direct client INSERT on audit.data_events
    // was found forgeable (any authenticated caller could self-attribute
    // an arbitrary event) and the grant has been revoked.
    // Operational Security Audit (2026-09-14), HIGH finding: this
    // previously fetched push_token straight into the browser via
    // get_owner_contacts_for_clinic and POSTed to Expo directly with it —
    // that RPC has never had a column-level restriction, so every caller
    // of it (including the two read-only contact-display call sites
    // elsewhere in this file) received the raw token regardless of
    // whether they needed to send anything. push_token has been removed
    // from that RPC's return columns entirely (migration 227); sending
    // now goes through a dedicated server-side function that verifies the
    // caller is an active member of the clinic that actually rejected
    // this pet before it will touch the token at all.
    try {
      const { error: notifyErr } = await supabase.functions.invoke('notify-enrollment-rejected', {
        body: { pet_id: pet.id, clinic_id: clinic.id },
      });
      if (notifyErr) console.warn('Rejection push notification failed (non-fatal):', notifyErr);
    } catch (_notifErr) { console.warn('Rejection push notification failed (non-fatal):', _notifErr); }
    fetchPendingPets(clinic.id);
    fetchRejectedPets(clinic.id);
  };

  const handleReverseRejection = async (pet) => {
    if (processingPetIds.has(pet.id)) return;
    setProcessingPetIds(prev => new Set(prev).add(pet.id));
    try {
      await handleReverseRejectionInner(pet);
    } finally {
      setProcessingPetIds(prev => { const next = new Set(prev); next.delete(pet.id); return next; });
    }
  };

  const handleReverseRejectionInner = async (pet) => {
    // Was a raw client update + a separate client-side audit insert; both
    // moved into reverse_pet_enrollment_rejection (migration 226) — the
    // raw update had no authorization check of its own (relied entirely
    // on the pet_clinic_relationships RLS policy), and the direct audit
    // insert was one of five call sites found to make the ledger
    // forgeable by any authenticated client.
    const { error: reverseErr } = await supabase.rpc('reverse_pet_enrollment_rejection', {
      p_pet_id:    pet.id,
      p_clinic_id: clinic.id,
    });
    if (reverseErr) {
      console.warn('reverse_pet_enrollment_rejection failed:', reverseErr);
      alert(t('mainView.reverseRejectionFailedGeneric'));
      return;
    }
    fetchPendingPets(clinic.id);
    fetchRejectedPets(clinic.id);
  };

  // Clinic-accept-without-a-disease-workflow ("wellness_linked", migration
  // 104) — purely a pet_clinic_relationships state change, deliberately
  // decoupled from whether the owner has separately enabled General
  // Health tracking on their own pet (docs/WELLNESS_WORKFLOW_REPORT_2026-07-24.md,
  // Decision 2). No conditions row is created or touched here — the
  // Wellness dashboard section reads whatever General Health data already
  // exists for the pet, which may be sparse if the owner hasn't engaged
  // with it. Single-table update, already correctly RLS-scoped to doctors
  // only (migration 100) — no RPC needed, this is Category A per this
  // week's architecture report's own classification (simple, low-risk,
  // no multi-table consistency requirement).
  const WELLNESS_ONLY_SENTINEL = '__wellness_only__';

  const handleAcceptWellnessOnly = async (pet) => {
    setAssignLoading(true); setAssignError('');
    try {
      // Legal-risk audit follow-up (2026-09-10, migration 215): this used
      // to be a raw client-side update with no VCPR/telehealth-consent
      // check at all (wellness-only creates no condition, so it never
      // went through assign_workflow_to_pet's checks either). Now RPC-
      // mediated so the same first-enrollment evidence gate applies here.
      const { error } = await supabase.rpc('confirm_wellness_only_enrollment', {
        p_pet_id: pet.id,
        p_clinic_id: clinic.id,
      });
      if (error) throw error;

      setAssigningPet(null);
      fetchPendingPets(clinic.id);
      fetchRejectedPets(clinic.id);
      fetchWellnessPatients(clinic.id);
    } catch (err) {
      setAssignError(t('mainView.assignFailedGeneric'));
    } finally {
      setAssignLoading(false);
    }
  };

  const handleAssign = async (workflowId, vetId) => {
    if (workflowId === WELLNESS_ONLY_SENTINEL) {
      return handleAcceptWellnessOnly(assigningPet);
    }
    setAssignLoading(true); setAssignError('');
    try {
      const pet = assigningPet;
      const workflow = workflows.find(w => w.id === workflowId);
      if (!workflow) throw new Error('Workflow not found');

      // The relationship activation, condition create/reactivate, baseline
      // survey, and (for CHF) companion SRR condition+survey all used to be
      // separate sequential calls here — a failure partway through could
      // leave a condition active with no check-in ever scheduled. Now one
      // atomic RPC (migration 50); Postgres rolls the whole thing back
      // together if anything fails, so that partial state is no longer
      // possible.
      const { data: rpcResult, error: rpcErr } = await supabase.rpc('assign_workflow_to_pet', {
        p_pet_id:         pet.id,
        p_clinic_id:      clinic.id,
        p_template_id:    workflowId,
        p_condition_type: workflow.condition_type,
        p_vet_id:         vetId,
      });
      if (rpcErr) throw rpcErr;
      const cond = { id: rpcResult?.[0]?.condition_id };
      if (!cond.id) throw new Error('assign_workflow_to_pet did not return a condition_id');

      // Snapshot pet context at enrollment time — was previously only ever
      // filling current_medications, leaving species/sex/neuter_status/
      // diet_category/weight_at_enrollment/comorbidities columns permanently
      // null despite existing specifically to capture this.
      try {
        const [{ data: meds }, { data: petRow }, { data: latestWeight }, { data: latestDiet }, { data: otherConditions }] = await Promise.all([
          supabase.from('medication_entries').select('medication_name, dose, frequency').eq('pet_id', pet.id).eq('is_active', true),
          supabase.from('pets').select('species, breed, sex, neuter_status, date_of_birth').eq('id', pet.id).single(),
          supabase.from('weight_entries').select('weight, unit').eq('pet_id', pet.id).is('deleted_at', null).order('measured_at', { ascending: false }).limit(1).maybeSingle(),
          supabase.from('diet_entries').select('diet_category').eq('pet_id', pet.id).order('changed_at', { ascending: false }).limit(1).maybeSingle(),
          supabase.from('conditions').select('condition_type').eq('pet_id', pet.id).eq('is_active', true).neq('id', cond.id),
        ]);

        const ageYears = petRow?.date_of_birth
          ? (Date.now() - new Date(petRow.date_of_birth).getTime()) / (365.25 * 86400000)
          : null;

        await supabase.from('pet_enrollment_snapshots').insert({
          pet_id:                  pet.id,
          condition_id:            cond.id,
          clinic_linked:           true,
          current_medications:     meds || [],
          species:                 petRow?.species || null,
          breed_free_text:         petRow?.breed || null,
          sex:                     petRow?.sex || null,
          neuter_status:           petRow?.neuter_status || null,
          age_years_at_enrollment: ageYears !== null ? Math.round(ageYears * 10) / 10 : null,
          weight_at_enrollment:    latestWeight?.weight ?? null,
          weight_unit:             latestWeight?.unit ?? null,
          diet_category:           latestDiet?.diet_category ?? null,
          comorbidities:           (otherConditions || []).map(c => c.condition_type),
        });
      } catch (_snapErr) { console.warn('Enrollment snapshot failed (non-fatal):', _snapErr); }
      // pets.clinic_id sync and cross-clinic condition transfer used to be
      // two more non-fatal client-side steps here — both consistency-
      // critical (could silently drift from the relationship/condition
      // state the RPC above already committed if either failed), unlike
      // the snapshot/audit steps around them which are historical/
      // secondary. Both now happen inside assign_workflow_to_pet itself,
      // in the same transaction as everything else (architecture report
      // finding, 2026-07-23, migration 102) — a failure there rolls back
      // the whole assignment instead of leaving it half-consistent.
      // The enrollment_confirmed/template_assigned audit events are now
      // also logged from inside assign_workflow_to_pet itself (migration
      // 226) — using the actual caller (current_profile_id()), not vetId,
      // for the same reason the old client-side comment here warned
      // about: vetId is the doctor being ASSIGNED TO the workflow, not
      // necessarily the staff member performing the assignment, and using
      // it as actor_profile_id previously failed this table's RLS policy
      // silently for exactly that mismatch (architecture report, migration
      // 102). The direct client INSERT itself was also one of five call
      // sites found to make the ledger forgeable by any authenticated
      // client; the grant has been revoked.
      // Owner notification (enrollment confirmed, baseline check-in ready)
      // now fires server-side from inside assign_workflow_to_pet itself
      // (migration 237) -- in the same transaction as the condition being
      // created, not dependent on this browser tab staying open and a
      // separate fetch completing (audit #8 finding: the old fire-and-
      // forget invoke() here could silently lose the notification with no
      // retry if the tab closed first).
      setAssigningPet(null);
      fetchPendingPets(clinic.id);
      fetchRejectedPets(clinic.id);
      fetchPatients(clinic.id);
    } catch (err) {
      // assign_workflow_to_pet's billing-gate exception (migration 72) is
      // the one error here worth showing verbatim — everything else stays
      // generic since raw Postgres/network errors aren't useful to a
      // clinic user.
      setAssignError(
        err.message?.includes('billing is past due or canceled')
          ? err.message
          : t('mainView.assignFailedGeneric')
      );
    } finally {
      setAssignLoading(false);
    }
  };

  if (authChecking) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: c.fog, fontSize: 13, color: c.textSoft, fontFamily: 'Inter, system-ui, sans-serif' }}>{t('common.loading')}</div>;
  if (needsPasswordSetup) return <SetPasswordScreen />;
  if (needsProfileSetup) return <CompleteClinicSignupScreen />;
  if (pendingMfaContext) return <MfaGate onVerified={handleMfaVerified} onLogout={handleLogout} />;
  // 2026-08-27 (client-quality audit) — a hydration error/timeout is
  // distinct from a confirmed "no session": don't show LoginScreen (which
  // reads as "you got logged out") for what might just be a network blip.
  if (!profile && hydrationError) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, backgroundColor: c.fog, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ fontSize: 13, color: c.textSoft, maxWidth: 360, textAlign: 'center' }} role="alert">{t('common.authCheckFailed')}</div>
      <button
        onClick={() => { setHydrationError(null); setAuthChecking(true); checkAuth(); }}
        style={{ padding: '8px 20px', borderRadius: 6, border: 'none', backgroundColor: c.moss, color: '#fff', fontSize: 13, cursor: 'pointer' }}
      >
        {t('common.retry')}
      </button>
    </div>
  );
  // Self-serve signup (2026-09-10): app.cairnpethealth.com/signup lands
  // straight in signup mode, matching the sketch doc's recommended URL —
  // this app has no client-side router, so this is a one-time pathname
  // check rather than a real route.
  // Hydration-time invite recovery (clinic-onboarding audit finding): a
  // returning invitee whose earlier acceptance failed for lack of seats
  // lands straight back on the real accept/decline screen instead of a
  // bare, unexplained login form. LoginScreen owns all of the actual
  // accept/decline/error-handling logic already (handleAcceptInvite) —
  // this just seeds its local pendingInvite state on mount instead of
  // requiring the user to blindly re-submit the login form to rediscover
  // it via its own separate query.
  if (!profile && hydratedPendingInvite) return <LoginScreen onLogin={handleLogin} initialPendingInvite={hydratedPendingInvite} />;
  if (!profile)     return <LoginScreen onLogin={handleLogin} initialMode={window.location.pathname === '/signup' ? 'signup' : 'login'} />;

  // Legal-risk audit (2026-09-10), finding #1: block every clinical screen
  // until a first-time Doctor personally attests. Covers all three ways a
  // profile can end up with an active is_doctor row -- a brand-new invite
  // (SetPasswordScreen's consume_pending_clinic_invite), an existing
  // account accepting an invite (handleAcceptInvite above), or a Manager
  // flipping the Doctor checkbox for an already-active member in
  // PermissionsView -- since this check runs on every hydration/login,
  // not inside any one of those three RPCs. Also re-shows the gate on a
  // version mismatch (a future copy update), not just a missing
  // timestamp -- the server (is_active_clinic_doctor, migration 215)
  // requires an exact version match too, so a stale client-side check
  // here would otherwise let a re-attestation-due Doctor sit on a
  // dashboard where every clinical RPC/RLS read now silently fails.
  if (membership?.is_doctor && (
    !membership.clinical_attestation_at
    || membership.clinical_attestation_version !== CLINICAL_ATTESTATION_VERSION
  )) {
    return (
      <ClinicalRoleAttestationScreen
        onAttested={() => setMembership(prev => ({
          ...prev,
          clinical_attestation_at: new Date().toISOString(),
          clinical_attestation_version: CLINICAL_ATTESTATION_VERSION,
        }))}
        onLogout={handleLogout}
      />
    );
  }

  // Three roles as of the 2026-08-28 roles-check decision: Doctor
  // (patient clinical data + workflow assignment, counts toward
  // subscription seats), Manager (staff invites, billing, support queue,
  // clinic settings/deletion, finalizing/reassigning any patient,
  // doesn't count toward seats), and the separate global Super admin
  // (profiles.is_superadmin, held for later). is_admin (migration 75's
  // "deliberately dormant" fourth role) is fully retired as of migration
  // 169 — anyone who needs both Doctor and Manager access on one clinic
  // now just has both is_doctor and is_manager set, which the schema
  // already allowed independently.
  const canClinical = !!membership?.is_doctor;
  // Superadmin (profiles.is_superadmin) gets manager-equivalent access
  // everywhere — RLS already scopes what that actually returns per table;
  // this only controls nav/view visibility.
  const canManage   = !!(membership?.is_manager || profile?.is_superadmin);

  // filtered/grouped are computed above via useMemo (before the early
  // returns) — see the comment there.
  // Dismiss is a queue-position change only (2026-09-08 correction) — it
  // never touches severity/color, so a pet with a dismissed alert still
  // carries its true (worst) severity via groupPatients. Section
  // placement is a separate question: a pet only stays in its natural
  // severity section if at least one of its conditions has a live,
  // non-dismissed open alert; otherwise (everything dismissed, or no
  // alert at all) it falls through. A pet with a mix — one dismissed
  // condition, one still genuinely active — correctly stays in its active
  // section, since something still needs attention.
  // Merged 2026-09-09 (Bryan): Snooze is gone (folded into one "Mark
  // reviewed" action alongside Dismiss/Acknowledge — see alertActions.js)
  // — no more separate Snoozed section or isSnoozed check here.
  const hasActiveNonDismissedAlert = pg => pg.conditions.some(r => r.alert && !r.alert.dismissed_at);
  const hasAnyDismissedAlert = pg => pg.conditions.some(r => r.alert && r.alert.dismissed_at);
  const immediateGroups = grouped.filter(pg => pg.severity === 'immediate_review' && hasActiveNonDismissedAlert(pg));
  const redGroups    = grouped.filter(pg => pg.severity === 'red' && hasActiveNonDismissedAlert(pg));
  const yellowGroups = grouped.filter(pg => pg.severity === 'yellow' && hasActiveNonDismissedAlert(pg));
  const recentlyReviewedGroups = grouped.filter(pg => !hasActiveNonDismissedAlert(pg) && hasAnyDismissedAlert(pg));
  const greenGroups  = grouped.filter(pg => pg.severity === 'green' && !hasAnyDismissedAlert(pg));
  const conditionTypes = [...new Set(workflows.map(w => w.condition_type).filter(Boolean))].sort();

  return (
    <div className="dashboard-shell" style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', backgroundColor: c.fog }}>
      <Sidebar view={view} setView={setView} pendingCount={pendingPets.length} onLogout={handleLogout} onToggleUnit={handleToggleUnit} onToggleLang={handleToggleLang} onSetDefaultConditionFilter={handleSetDefaultConditionFilter} conditionTypes={conditionTypes} profile={profile} clinic={clinic} patients={patients} vets={vets} membership={membership} canClinical={canClinical} canManage={canManage} />

      {/* Main content */}
      <div className="dashboard-main" style={{ marginLeft: 220, flex: 1, minWidth: 0 }}>
        <div className="dashboard-content" style={{ padding: '28px 32px', maxWidth: 1100 }}>

          {/* Billing success message after Stripe redirect */}
          {billingMsg === 'success' && (
            <div style={{ backgroundColor: c.greenLight, border: `1px solid ${c.moss}`, borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: c.moss, fontWeight: 500 }}>
                {t('mainView.subscriptionActivated')}
              </span>
              <button onClick={() => { setBillingMsg(''); refreshSubscription(clinic.id); }} style={{ fontSize: 12, color: c.moss, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>{t('patientCard.dismiss')}</button>
            </div>
          )}

          {/* Past-due / over-limit banners — deliberately different severity
              treatment (Audit §18.3 MEDIUM): past-due/canceled risks real
              clinical data sitting invisible, over-limit only means some
              doctors lose dashboard access. Same soft sandstone tone for
              both previously risked staff under-weighting the one that
              actually matters more. */}
          {/* past_due grace period is 14 days (over_limit below stays at 7 —
              different problem: a card declining vs. a manager assigning too
              many doctors). Once expired, the dashboard itself stops
              surfacing anything dated after the grace-end instant
              (freezeCutoff, computed below) — previously collected data
              stays fully visible, nothing is deleted, and restoring the
              subscription clears past_due_since and instantly unfreezes
              everything. Weekly reminder emails to the clinic's managers
              run via send-billing-dunning-emails for as long as
              past_due_since stays set, independent of this banner. */}
          {clinic?.billing_program !== 'beta' && subscription?.past_due_since && pastDueGraceEnd && (() => {
            // Derived from the same pastDueGraceEnd Date freezeCutoff itself
            // uses, not recomputed independently — see that memo's comment.
            const daysLeft = Math.max(0, Math.ceil((pastDueGraceEnd - Date.now()) / 86400000));
            // A direct/voluntary cancellation reuses the same past_due_since
            // clock and freeze mechanism (stripe-webhook's
            // handleSubscriptionDeleted) — this distinguishes the copy so it
            // doesn't misleadingly read as a billing/card-decline error.
            const isCanceled = subscription?.status === 'canceled';
            return (
              <div style={{ backgroundColor: c.redLight, border: `1px solid ${c.red}`, borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                <p style={{ fontSize: 13, color: c.red, fontWeight: 600, margin: '0 0 2px' }}>{isCanceled ? t('mainView.subscriptionCanceled') : t('mainView.paymentPastDue')}</p>
                <p style={{ fontSize: 12, color: c.red, margin: 0 }}>
                  {daysLeft > 0
                    ? (isCanceled
                        ? t('mainView.resubscribeDaysLeft', { count: daysLeft })
                        : t('mainView.updatePaymentDaysLeft', { count: daysLeft }))
                    : t('mainView.subscriptionLapsed')}
                  <button onClick={() => setView('billing')} style={{ fontSize: 12, color: c.red, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{t('mainView.goToBilling')}</button>
                </p>
              </div>
            );
          })()}

          {/* Unlike past_due_since (which the dashboard itself enforces via
              freezeCutoff, a read-side filter), over_limit_since is
              enforced entirely server-side by enforce-clinic-seat-limits
              (a daily cron deactivating the most-recently-added doctors
              over the seat count, 7-day grace period). The banner
              previously claimed "new data is paused" once the grace period
              ended — inaccurate: nothing about this condition ever pauses
              data, it deactivates specific doctors' access. Fixed to
              describe what actually happens. */}
          {clinic?.billing_program !== 'beta' && subscription?.over_limit_since && (() => {
            const daysLeft = Math.max(0, 7 - Math.floor((Date.now() - new Date(subscription.over_limit_since)) / 86400000));
            return (
              <div style={{ backgroundColor: c.sandstoneLight, border: `1px solid ${c.sandstone}`, borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                <p style={{ fontSize: 13, color: c.sandstoneText, fontWeight: 600, margin: '0 0 2px' }}>{t('mainView.doctorCountExceeds')}</p>
                <p style={{ fontSize: 12, color: c.sandstoneText, margin: 0 }}>
                  {daysLeft > 0
                    ? t('mainView.addSeatsWithinDays', { count: daysLeft })
                    : t('mainView.gracePeriodEnded')}
                  <button onClick={() => setView('billing')} style={{ fontSize: 12, color: c.sandstoneText, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{t('mainView.goToBilling')}</button>
                  {' · '}
                  <button onClick={() => setView('permissions')} style={{ fontSize: 12, color: c.sandstoneText, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{t('mainView.adjustPermissions')}</button>
                </p>
              </div>
            );
          })()}

          {view === 'dashboard' && (
            <DashboardView
              canClinical={canClinical}
              canManage={canManage}
              clinic={clinic}
              subscription={subscription}
              subscriptionLoaded={subscriptionLoaded}
              refreshSubscription={refreshSubscription}
              pendingPets={pendingPets}
              setView={setView}
              loading={loadingDashboard}
              lastUpdated={lastUpdated}
              onRefresh={handleDashboardMount}
              onMount={handleDashboardMount}
              filters={filters}
              setFilters={setFilters}
              vets={vets}
              conditionTypes={conditionTypes}
              profile={profile}
              onSetDefault={handleSetDefaultConditionFilter}
              patientsError={patientsError}
              hasPatients={patients.length > 0}
              immediateGroups={immediateGroups}
              redGroups={redGroups}
              yellowGroups={yellowGroups}
              recentlyReviewedGroups={recentlyReviewedGroups}
              greenGroups={greenGroups}
              mutedGroups={mutedGroups}
              wellnessPatients={wellnessPatients}
              wellnessPatientsError={wellnessPatientsError}
              recoveryThresholds={recoveryThresholds}
              onSelect={setSelectedPatient}
              onDismissAlert={handleDismissAlert}
              onReactivateAlert={handleReactivateAlert}
              onPrintSummary={handlePrintSummary}
              selectedPatient={selectedPatient}
              useLbs={useLbs}
              freezeCutoff={freezeCutoff}
              isInCatchUpWindow={isInCatchUpWindow}
              onReassignDoctor={handleReassignDoctor}
              onAssignCondition={(p) => { setAssignError(''); setAssigningPet({ ...p.pet, profiles: p.owner }); }}
            />
          )}

          {view === 'enrollments' && (
            <EnrollmentsView
              onMount={handleEnrollmentsMount}
              loading={loadingEnrollments}
              pendingError={pendingError}
              pendingPets={pendingPets}
              canClinical={canClinical}
              onConfirm={pet => {
                setAssignError('');
                // Legal-risk audit finding #2 (2026-09-10): gate the first
                // condition assignment for a pending enrollment on a VCPR
                // attestation. Already-enrolled patients getting an
                // additional condition later (onAssignCondition, below)
                // don't re-attest -- the relationship was already
                // confirmed at enrollment.
                if (!pet.vcprAttestedAt) { setVcprPendingPet(pet); return; }
                setAssigningPet(pet);
              }}
              onReject={handleReject}
              rejectedError={rejectedError}
              rejectedPets={rejectedPets}
              rejectedExpanded={rejectedExpanded}
              setRejectedExpanded={setRejectedExpanded}
              onReverseRejection={handleReverseRejection}
              processingPetIds={processingPetIds}
            />
          )}

          {view === 'workflows' && (
            <div style={{ backgroundColor: c.white, borderRadius: 12, padding: 48, textAlign: 'center', color: c.textSoft, border: `1px solid ${c.border}` }}>
              {t('mainView.workflowsComingSoon')}
            </div>
          )}

          {view === 'permissions' && canManage && (
            <PermissionsView clinic={clinic} profile={profile} onGoToBilling={() => setView('billing')} />
          )}

          {view === 'permissions' && !canManage && (
            <div style={{ backgroundColor: c.white, borderRadius: 12, padding: 48, textAlign: 'center', color: c.textSoft, border: `1px solid ${c.border}` }}>
              <p style={{ fontSize: 15, fontWeight: 500, color: c.slate, marginBottom: 8 }}>{t('mainView.accessRestricted')}</p>
              <p style={{ fontSize: 13 }}>{t('mainView.permissionsRestrictedBody')}</p>
            </div>
          )}

          {view === 'billing' && (
            <BillingView
              clinic={clinic}
              membership={membership}
              subscription={subscription}
              subscriptionLoaded={subscriptionLoaded}
              subscriptionError={subscriptionError}
              onRefresh={() => refreshSubscription(clinic.id)}
            />
          )}

          {view === 'settings' && (
            <ClinicSettingsView clinic={clinic} canManage={canManage} onClinicUpdate={setClinic} />
          )}

          {view === 'support' && canManage && (
            <SupportView profile={profile} clinic={clinic} />
          )}

          {view === 'support' && !canManage && (
            <div style={{ backgroundColor: c.white, borderRadius: 12, padding: 48, textAlign: 'center', color: c.textSoft, border: `1px solid ${c.border}` }}>
              <p style={{ fontSize: 15, fontWeight: 500, color: c.slate, marginBottom: 8 }}>{t('mainView.accessRestricted')}</p>
              <p style={{ fontSize: 13 }}>{t('mainView.supportRestrictedBody')}</p>
            </div>
          )}

        </div>

        <div style={{ textAlign: 'center', padding: '20px 32px', fontSize: 11, color: c.textSoft, borderTop: `1px solid ${c.border}`, marginTop: 32 }}>
          {t('mainView.footerDisclaimer')}
        </div>
      </div>

      {selectedPatient && (
        <PatientDetailPanel
          // Keyed on pet+condition identity — without this, clicking
          // straight from one patient to another (no close in between)
          // reuses the SAME component instance, and every one of this
          // panel's ~7 independent data-fetching useEffects (surveys,
          // weights, medications, intervention events, etc.) has no
          // cancellation/staleness guard against out-of-order network
          // responses. Two rapid switches (e.g. TestCHF -> Purrcy) fire two
          // overlapping fetches per effect; whichever happens to resolve
          // LAST wins, regardless of which one was actually requested last
          // — so the panel intermittently ends up showing the PREVIOUS
          // patient's chart/data under the new patient's header, or a mix.
          // Exactly the reported "sometimes it loads, sometimes it doesn't"
          // pattern (2026-08-10) — a race, not a deterministic bug, which
          // is why it wasn't consistent. A key forces React to fully
          // unmount the old instance and mount a fresh one on every
          // identity change: state resets to initial values immediately,
          // and any late-arriving response from the orphaned old instance
          // writes into a component that's no longer rendered, so it can
          // never clobber the new one. Cheaper and far less error-prone
          // than adding a manual staleness check to each of the 7 effects
          // individually.
          key={`${selectedPatient.pet.id}:${selectedPatient.condition?.id || ''}`}
          patient={selectedPatient}
          clinicId={clinic?.id}
          profile={profile}
          vets={vets}
          recoveryThresholds={recoveryThresholds}
          useLbs={useLbs}
          freezeCutoff={freezeCutoff}
          isInCatchUpWindow={isInCatchUpWindow}
          canManage={canManage}
          canClinical={canClinical}
          onClose={() => setSelectedPatient(null)}
          onDismissAlert={handleDismissAlert}
          onReactivateAlert={handleReactivateAlert}
          onPrintSummary={handlePrintSummary}
          onReassignDoctor={handleReassignDoctor}
          onPatientFinalized={() => fetchPatients(clinic.id)}
          onAssignCondition={(p) => { setAssignError(''); setAssigningPet({ ...p.pet, profiles: p.owner }); }}
        />
      )}

      {assigningPet && (
        <AssignModal
          pet={assigningPet}
          workflows={workflows}
          vets={vets}
          onAssign={handleAssign}
          onCancel={() => setAssigningPet(null)}
          loading={assignLoading}
          error={assignError}
        />
      )}

      {vcprPendingPet && (
        <VCPRAttestationModal
          pet={vcprPendingPet}
          onCancel={() => setVcprPendingPet(null)}
          onAttested={() => {
            const pet = vcprPendingPet;
            setVcprPendingPet(null);
            setPendingPets(prev => prev.map(p => p.id === pet.id ? { ...p, vcprAttestedAt: new Date().toISOString() } : p));
            setAssigningPet({ ...pet, vcprAttestedAt: new Date().toISOString() });
          }}
        />
      )}
    </div>
  );
}
